/** The rule of rule U-35 for a new version: CURRENT, then PENDING, then DUE, then RELOADING. No timers, no DOM. */

export type Phase = "current" | "pending" | "due" | "reloading";
export type DueWhy = "break" | "night" | "day";
export type ReloadReason = "load" | "navigate" | "hidden" | "idle" | "user" | `due_${DueWhy}`;

export interface PolicyState {
  phase: Phase;
  target: string | null;
  seenAt: number | null;
  nightAt: number | null;
  breakDue: boolean;
  dueWhy: DueWhy | null;
  countdownEnd: number | null;
  countdownMs: number | null;
  canSnooze: boolean;
  snoozes: number;
  snoozedUntil: number | null;
  holdSince: number | null;
  heldLogged: boolean;
}

export interface Context {
  now: number;
  /** The time when the tab was hidden; null while it shows. */
  hiddenSince: number | null;
  /** The last key, pointer or touch of the user. */
  lastActive: number;
  /** The labels of the dirty forms with no draft. */
  holds: string[];
  /** The user typed in the page on screen. */
  edited: boolean;
  /** The outbox entries that wait to be sent. */
  unsent: number;
  /** The loop guard stops the reloads for a time. */
  guarded: boolean;
}

export type Trigger = "load" | "navigate" | "tick" | "visible";

export type PolicyEvent =
  | { type: "new_build"; target: string; serverBreak: number; ownBreak: number; jitterMs: number }
  | { type: "current" }
  | { type: "check"; trigger: Trigger }
  | { type: "snooze" }
  | { type: "update_now" };

export type Effect =
  | { kind: "pending"; target: string; nightAt: number }
  | { kind: "due"; why: DueWhy }
  | { kind: "countdown"; ms: number; snooze: boolean }
  | { kind: "snooze"; count: number; until: number }
  | { kind: "deferred"; reasons: string[]; at: "silent" | "due" }
  | { kind: "held_long"; hours: number; labels: string[] }
  | { kind: "reload"; reason: ReloadReason };

export const NIGHT_HOUR = 3;
export const JITTER_MAX_MS = 10 * 60_000;
export const PENDING_MAX_MS = 24 * 3_600_000;
export const HIDDEN_MS = 30 * 60_000;
export const IDLE_MS = 15 * 60_000;
export const ACTIVE_MS = 2 * 60_000;
export const COUNTDOWN_MS = 60_000;
export const ACTIVE_COUNTDOWN_MS = 5 * 60_000;
export const SNOOZE_MS = 30 * 60_000;
export const SNOOZE_MAX = 2;
export const HELD_LONG_MS = 24 * 3_600_000;

export const CURRENT: PolicyState = {
  phase: "current",
  target: null,
  seenAt: null,
  nightAt: null,
  breakDue: false,
  dueWhy: null,
  countdownEnd: null,
  countdownMs: null,
  canSnooze: false,
  snoozes: 0,
  snoozedUntil: null,
  holdSince: null,
  heldLogged: false,
};

/** The first 03:00 of the device clock after `after`, plus the jitter. A Date in local time keeps the hour across a clock change. */
export function nextNight(after: number, jitterMs: number): number {
  const day = new Date(after);
  const night = new Date(day.getFullYear(), day.getMonth(), day.getDate(), NIGHT_HOUR, 0, 0, 0);
  if (night.getTime() <= after) night.setDate(night.getDate() + 1);
  night.setHours(NIGHT_HOUR, 0, 0, 0);
  return night.getTime() + Math.min(Math.max(jitterMs, 0), JITTER_MAX_MS);
}

/** The safe moment of a silent reload, or null. */
function silentMoment(trigger: Trigger, ctx: Context): ReloadReason | null {
  if (trigger === "load" || trigger === "navigate") return trigger;
  if (ctx.hiddenSince !== null && ctx.now - ctx.hiddenSince >= HIDDEN_MS) return "hidden";
  if (ctx.hiddenSince === null && ctx.now - ctx.lastActive >= IDLE_MS) return "idle";
  return null;
}

function dueWhy(state: PolicyState, now: number): DueWhy | null {
  if (state.breakDue) return "break";
  if (state.nightAt !== null && now >= state.nightAt) return "night";
  if (state.seenAt !== null && now - state.seenAt >= PENDING_MAX_MS) return "day";
  return null;
}

const reload = (state: PolicyState, reason: ReloadReason, effects: Effect[]) => ({
  state: { ...state, phase: "reloading" as const, countdownEnd: null },
  effects: [...effects, { kind: "reload", reason } as Effect],
});

/** Move PENDING and DUE on: a silent reload at a safe moment, the countdown when due, or a wait for a form. */
function evaluate(state: PolicyState, trigger: Trigger, ctx: Context, effects: Effect[]): { state: PolicyState; effects: Effect[] } {
  if (state.phase !== "pending" && state.phase !== "due") return { state, effects };
  if (ctx.guarded) return { state, effects };
  const moment = silentMoment(trigger, ctx);
  if (moment) {
    const reasons = [...ctx.holds.map((label) => `form:${label}`), ...(ctx.edited ? ["edited"] : []), ...(ctx.unsent > 0 ? [`unsent:${ctx.unsent}`] : [])];
    if (!reasons.length) return reload(state, moment, effects);
    effects.push({ kind: "deferred", reasons, at: "silent" });
  }
  let next = state;
  if (next.phase === "pending") {
    const why = dueWhy(next, ctx.now);
    if (!why) return { state: next, effects };
    next = { ...next, phase: "due", dueWhy: why };
    effects.push({ kind: "due", why });
  }
  if (ctx.holds.length) {
    const holdSince = next.holdSince ?? ctx.now;
    next = { ...next, holdSince, countdownEnd: null, countdownMs: null };
    if (state.holdSince === null) effects.push({ kind: "deferred", reasons: ctx.holds.map((label) => `form:${label}`), at: "due" });
    if (!next.heldLogged && ctx.now - holdSince >= HELD_LONG_MS) {
      next = { ...next, heldLogged: true };
      effects.push({ kind: "held_long", hours: Math.floor((ctx.now - holdSince) / 3_600_000), labels: ctx.holds });
    }
    return { state: next, effects };
  }
  next = { ...next, holdSince: null };
  if (next.snoozedUntil !== null && ctx.now < next.snoozedUntil) return { state: next, effects };
  if (next.countdownEnd === null) {
    const active = ctx.hiddenSince === null && ctx.now - ctx.lastActive < ACTIVE_MS;
    const ms = active ? ACTIVE_COUNTDOWN_MS : COUNTDOWN_MS;
    const canSnooze = active && !next.breakDue && next.snoozes < SNOOZE_MAX;
    next = { ...next, countdownEnd: ctx.now + ms, countdownMs: ms, canSnooze, snoozedUntil: null };
    effects.push({ kind: "countdown", ms, snooze: canSnooze });
    return { state: next, effects };
  }
  if (ctx.now >= next.countdownEnd) return reload(next, `due_${next.dueWhy ?? "day"}`, effects);
  return { state: next, effects };
}

/** One step of the rule. */
export function step(state: PolicyState, event: PolicyEvent, ctx: Context): { state: PolicyState; effects: Effect[] } {
  const effects: Effect[] = [];
  switch (event.type) {
    case "new_build": {
      if (state.phase === "reloading") return { state, effects };
      const breakDue = state.breakDue || event.serverBreak > event.ownBreak;
      if (state.phase === "current") {
        const nightAt = nextNight(ctx.now, event.jitterMs);
        effects.push({ kind: "pending", target: event.target, nightAt });
        return evaluate({ ...CURRENT, phase: "pending", target: event.target, seenAt: ctx.now, nightAt, breakDue }, "tick", ctx, effects);
      }
      return evaluate({ ...state, target: event.target, breakDue }, "tick", ctx, effects);
    }
    case "current":
      return { state: state.phase === "reloading" ? state : CURRENT, effects };
    case "check":
      return evaluate(state, event.trigger, ctx, effects);
    case "snooze": {
      if (state.phase !== "due" || state.countdownEnd === null || !state.canSnooze) return { state, effects };
      const snoozes = state.snoozes + 1;
      const until = ctx.now + SNOOZE_MS;
      effects.push({ kind: "snooze", count: snoozes, until });
      return { state: { ...state, snoozes, snoozedUntil: until, countdownEnd: null, countdownMs: null, canSnooze: false }, effects };
    }
    case "update_now":
      if ((state.phase !== "pending" && state.phase !== "due") || ctx.holds.length) return { state, effects };
      return reload(state, "user", effects);
  }
}
